import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CreateTeamInput,
  Team,
  UpdateMemberInput,
  UpdateTeamInput,
} from '@this+that/models';

const TEAM_FIELDS = gql`
  fragment TeamFields on Team {
    id
    userId
    userRole
    userFeatures
    userName
    userEmail
    userTitle
    name
    hexColor
    createdFor
    createdAt
    trialEndsAt
    trialHandedOffAt
    consultingTeam
    subscriptionStatus
    stripeTrialEnd
    planInterval
    seatQuantity
    cancelAtPeriodEnd
    priceUnitAmount
    priceCurrency
  }
`;

const TEAM_SUMMARY_FIELDS = gql`
  fragment TeamSummaryFields on Team {
    id
    userId
    userRole
    userName
    userEmail
    userTitle
    name
    hexColor
    createdFor
    createdAt
    trialEndsAt
    trialHandedOffAt
    consultingTeam
    subscriptionStatus
    stripeTrialEnd
    planInterval
    seatQuantity
    cancelAtPeriodEnd
    priceUnitAmount
    priceCurrency
  }
`;

interface GetTeamByIdData {
  getTeamById: Team;
}

interface GetPaginatedTeamMembersData {
  getPaginatedTeamMembers: {
    members: Team[];
    exclusiveStartKey?: string;
  };
}

interface GetUserTeamIdsData {
  getUserTeamIds: string[];
}

interface CreateTeamData {
  createTeam: Team;
}

interface UpdateTeamData {
  updateTeam: Team[];
}

interface DeleteTeamData {
  deleteTeam: boolean;
}

interface UpdateMemberData {
  updateMember: Team;
}

interface RemoveMemberData {
  removeMember: boolean;
}

const GET_TEAM_BY_ID = gql`
  query getTeamById($id: String!) {
    getTeamById(id: $id) {
      ...TeamFields
    }
  }
  ${TEAM_FIELDS}
`;

export function useGetTeamById() {
  const { loading, refetch } = useQuery<GetTeamByIdData>(GET_TEAM_BY_ID, {
    skip: true,
  });

  const getTeamById = useCallback(
    async (id: string) => {
      const { data } = await refetch({ id });
      return data?.getTeamById;
    },
    [refetch],
  );

  return { getTeamById, loading };
}

const GET_PAGINATED_TEAM_MEMBERS = gql`
  query getPaginatedTeamMembers(
    $id: String!
    $limit: Int!
    $exclusiveStartKey: String
  ) {
    getPaginatedTeamMembers(
      id: $id
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
    ) {
      members {
        ...TeamFields
      }
      exclusiveStartKey
    }
  }
  ${TEAM_FIELDS}
`;

export function useGetPaginatedTeamMembers() {
  const { loading, refetch } = useQuery<GetPaginatedTeamMembersData>(
    GET_PAGINATED_TEAM_MEMBERS,
    {
      skip: true,
    },
  );

  const getPaginatedTeamMembers = useCallback(
    async (id: string, limit: number, exclusiveStartKey?: string) => {
      const { data } = await refetch({ id, limit, exclusiveStartKey });
      return data?.getPaginatedTeamMembers;
    },
    [refetch],
  );

  return { getPaginatedTeamMembers, loading };
}

const GET_USER_TEAM_IDS = gql`
  query getUserTeamIds {
    getUserTeamIds
  }
`;

export function useGetUserTeamIds() {
  const { loading, refetch } = useQuery<GetUserTeamIdsData>(GET_USER_TEAM_IDS, {
    skip: true,
  });

  const getUserTeamIds = useCallback(async () => {
    const { data } = await refetch();
    return data?.getUserTeamIds;
  }, [refetch]);

  return { getUserTeamIds, loading };
}

const CREATE_TEAM = gql`
  mutation createTeam($team: CreateTeamInput!) {
    createTeam(team: $team) {
      ...TeamSummaryFields
    }
  }
  ${TEAM_SUMMARY_FIELDS}
`;

export function useCreateTeam() {
  const [createTeamMutation, { loading, error }] =
    useMutation<CreateTeamData>(CREATE_TEAM);

  const createTeam = useCallback(
    async (team: CreateTeamInput) => {
      const { data } = await createTeamMutation({
        variables: { team },
      });
      return data?.createTeam;
    },
    [createTeamMutation],
  );

  return { createTeam, loading, error };
}

interface CreateConsultingTeamData {
  createConsultingTeam: Team;
}

const CREATE_CONSULTING_TEAM = gql`
  mutation createConsultingTeam($team: CreateTeamInput!) {
    createConsultingTeam(team: $team) {
      ...TeamSummaryFields
    }
  }
  ${TEAM_SUMMARY_FIELDS}
`;

export function useCreateConsultingTeam() {
  const [createConsultingTeamMutation, { loading, error }] =
    useMutation<CreateConsultingTeamData>(CREATE_CONSULTING_TEAM);

  const createConsultingTeam = useCallback(
    async (team: CreateTeamInput) => {
      const { data } = await createConsultingTeamMutation({
        variables: { team },
      });
      return data?.createConsultingTeam;
    },
    [createConsultingTeamMutation],
  );

  return { createConsultingTeam, loading, error };
}

const UPDATE_TEAM = gql`
  mutation updateTeam($team: UpdateTeamInput!) {
    updateTeam(team: $team) {
      ...TeamSummaryFields
    }
  }
  ${TEAM_SUMMARY_FIELDS}
`;

export function useUpdateTeam() {
  const [updateTeamMutation, { loading, error }] =
    useMutation<UpdateTeamData>(UPDATE_TEAM);

  const updateTeam = useCallback(
    async (team: UpdateTeamInput) => {
      const { data } = await updateTeamMutation({
        variables: { team },
      });
      return data?.updateTeam;
    },
    [updateTeamMutation],
  );

  return { updateTeam, loading, error };
}

const DELETE_TEAM = gql`
  mutation deleteTeam($id: String!) {
    deleteTeam(id: $id)
  }
`;

export function useDeleteTeam() {
  const [deleteTeamMutation, { loading, error }] =
    useMutation<DeleteTeamData>(DELETE_TEAM);

  const deleteTeam = useCallback(
    async (id: string) => {
      const { data } = await deleteTeamMutation({ variables: { id } });
      return data?.deleteTeam;
    },
    [deleteTeamMutation],
  );

  return { deleteTeam, loading, error };
}

const UPDATE_MEMBER = gql`
  mutation updateMember($memberInstance: UpdateMemberInput!) {
    updateMember(memberInstance: $memberInstance) {
      ...TeamFields
    }
  }
  ${TEAM_FIELDS}
`;

export function useUpdateMember() {
  const [updateMemberMutation, { loading, error }] =
    useMutation<UpdateMemberData>(UPDATE_MEMBER);

  const updateMember = useCallback(
    async (memberInstance: UpdateMemberInput) => {
      const { data } = await updateMemberMutation({
        variables: { memberInstance },
      });
      return data?.updateMember;
    },
    [updateMemberMutation],
  );

  return { updateMember, loading, error };
}

const REMOVE_MEMBER = gql`
  mutation removeMember($id: String!, $memberId: String!) {
    removeMember(id: $id, memberId: $memberId)
  }
`;

export function useRemoveMember() {
  const [removeMemberMutation, { loading, error }] =
    useMutation<RemoveMemberData>(REMOVE_MEMBER);

  const removeMember = useCallback(
    async (id: string, memberId: string) => {
      const { data } = await removeMemberMutation({
        variables: { id, memberId },
      });
      return data?.removeMember;
    },
    [removeMemberMutation],
  );

  return { removeMember, loading, error };
}
